import React, {
  FC, useEffect, useMemo, useRef,
} from 'react';
import _ from 'lodash';
import { RADIO_AUTO_ADVANCE_DELAY_MS } from '../../data/constants';
import type { QuestionProps } from './types';

export const ImpairmentQuestion: FC<QuestionProps> = ({
  question, answers, setAnswers, onComplete,
}) => {
  const currentAnswer = answers[question.id];
  const updateAnswer = (v: any) => setAnswers((p) => ({ ...p, [question.id]: v }));
  const options = question.options || [];

  const hasFollowUp = !!question.conditionalFollowUp;
  const triggerValues = useMemo(
    () => question.conditionalFollowUp?.triggerValues || [],
    [question.conditionalFollowUp?.triggerValues],
  );
  const followUpFields = question.conditionalFollowUp?.fields || [];

  const radioAnswer = hasFollowUp ? (currentAnswer?.answer ?? '') : currentAnswer;
  const isTriggered = triggerValues.includes(radioAnswer);

  const handleRadioSelect = (optValue: string) => {
    if (hasFollowUp) {
      setAnswers((p) => ({
        ...p,
        [question.id]: { ...(p[question.id] || {}), answer: optValue },
      }));
    } else {
      updateAnswer(optValue);
    }
  };

  const updateFollowUpField = (fieldId: string, value: string) => {
    setAnswers((p) => ({
      ...p,
      [question.id]: { ...(p[question.id] || {}), [fieldId]: value },
    }));
  };

  const handleToggleMultiSelect = (optValue: string) => {
    const cur: string[] = Array.isArray(currentAnswer) ? currentAnswer : [];
    const clickedOption = options.find((o) => String(o.value) === optValue);
    const isExclusive = clickedOption?.exclusive;

    if (isExclusive) {
      updateAnswer(cur.includes(optValue) ? [] : [optValue]);
    } else {
      const exclusiveValues = new Set(
        options.filter((o) => o.exclusive).map((o) => String(o.value)),
      );
      const withoutExclusive = cur.filter((v) => !exclusiveValues.has(v));
      updateAnswer(_.xor(withoutExclusive, [optValue]));
    }
  };

  const previousRadioAnswer = useRef(radioAnswer);
  const isRadio = question.type === 'radio';

  useEffect(() => {
    if (isRadio && radioAnswer && radioAnswer !== previousRadioAnswer.current) {
      previousRadioAnswer.current = radioAnswer;
      if (!isTriggered) {
        const t = setTimeout(onComplete, RADIO_AUTO_ADVANCE_DELAY_MS);
        return () => clearTimeout(t);
      }
    }
    previousRadioAnswer.current = radioAnswer;
    return undefined;
  }, [radioAnswer, isRadio, onComplete, isTriggered]);

  return (
    <div>
      <p className="text-base font-bold text-slate-800 mb-4 leading-snug">
        {question.text}
        {question.required && <span className="text-red-500 ml-1">*</span>}
      </p>
      {question.type === 'radio' && (
        <>
          <div className="flex flex-col gap-3">
            {options.map((o) => {
              const optValue = String(o.value);
              const sel = radioAnswer === optValue;
              return (
                <button
                  key={optValue}
                  type="button"
                  onClick={() => handleRadioSelect(optValue)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl border-2 text-sm font-medium text-left transition-all ${
                    sel
                      ? 'border-brand bg-brand bg-opacity-5 text-brand'
                      : 'border-slate-200 bg-white text-slate-700'
                  }`}
                >
                  <span
                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                      sel ? 'border-brand bg-brand' : 'border-slate-300 bg-white'
                    }`}
                  >
                    {sel && (
                      <span className="w-2 h-2 rounded-full bg-white block" />
                    )}
                  </span>
                  {o.label}
                </button>
              );
            })}
          </div>
          {isTriggered && followUpFields.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
              {followUpFields.map((field) => {
                const fieldId = `${question.id}-${field.id}`;
                return (
                  <div key={field.id}>
                    <label htmlFor={fieldId} className="block text-xs font-semibold text-slate-500 mb-1">
                      {field.label}
                      {field.required && <span className="text-red-500 ml-0.5" aria-hidden>*</span>}
                    </label>
                    {field.type === 'textarea' ? (
                      <textarea
                        id={fieldId}
                        aria-required={field.required || undefined}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-xs focus:outline-none resize-y min-h-16"
                        value={currentAnswer?.[field.id] || ''}
                        placeholder={field.placeholder}
                        onChange={(e) => updateFollowUpField(field.id, e.target.value)}
                      />
                    ) : (
                      <input
                        id={fieldId}
                        aria-required={field.required || undefined}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-xs focus:outline-none bg-white"
                        value={currentAnswer?.[field.id] || ''}
                        placeholder={field.placeholder}
                        onChange={(e) => updateFollowUpField(field.id, e.target.value)}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
      {question.type === 'checkbox' && (
        <div className="grid grid-cols-2 gap-2">
          {options.map((o) => {
            const optValue = String(o.value);
            const isSelected = Array.isArray(currentAnswer) && currentAnswer.includes(optValue);
            return (
              <button
                key={optValue}
                type="button"
                onClick={() => handleToggleMultiSelect(optValue)}
                className={`flex items-center gap-2 px-4 py-3 rounded-xl border-2 text-sm font-medium text-left transition-all ${
                  isSelected
                    ? 'border-brand bg-brand bg-opacity-5'
                    : 'border-slate-200 bg-white'
                }`}
              >
                <span
                  className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                    isSelected ? 'border-brand bg-brand' : 'border-slate-300 bg-white'
                  }`}
                >
                  {isSelected && (
                    <span className="text-white text-xs">✓</span>
                  )}
                </span>
                {o.label}
              </button>
            );
          })}
        </div>
      )}
      {question.type === 'text' && (
        <textarea
          className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl text-sm focus:outline-none resize-y min-h-28"
          placeholder="Describe here..."
          value={currentAnswer || ''}
          onChange={(e) => updateAnswer(e.target.value)}
        />
      )}
    </div>
  );
};

export default ImpairmentQuestion;
